import cn from 'classnames';
import { first } from 'lodash-es';
import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Study, StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { UploadSessionStatus } from '@/shared/api/protocol-ts/model/dto_upload_session_pb';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { getImageSrc } from '@/shared/lib';

import { ensureRefreshImageSrc } from '@/entities/assets';
import { getCBCTPreviewReport, reportsModel } from '@/entities/reports';
import { i18n } from '@/entities/study';

import { studyCardTitle } from '../../config';
import { UploadingStatusFrame } from '../UploadingStatusFrame/UploadingStatusFrame';

import styles from './StudyPreview.module.scss';

type StudyPreviewProps = {
  study: Study;
  className?: string;
};

const ALLOWED_REPORT_TYPES = [
  ReportType.ReportType_CBCT_GP,
  ReportType.ReportType_IOXRay_GP,
  ReportType.ReportType_Pano_GP,
  ReportType.ReportType_Pano_Bitewings,
  ReportType.ReportType_CBCT_Segmentation,
  ReportType.ReportType_CBCT_IOS_Superimposition,
  ReportType.ReportType_IOS_Segmentation,
  ReportType.ReportType_StudioImplant,
  ReportType.ReportType_StudioOrtho,
];

export const StudyPreview = (props: StudyPreviewProps) => {
  const { study, className } = props;

  const imagePreviewRef = useRef<HTMLDivElement>(null);

  const { formatMessage, formatDate } = useIntl();

  const { isPhone } = useMedia();

  const reports = useAppSelector(
    reportsModel.selectors.selectByStudyID(study.ID),
  );

  const firstRelevantReport = useMemo(() => {
    if (study.Type === StudyType.StudyType_CBCT) {
      return getCBCTPreviewReport(reports);
    }

    const filteredReports =
      study.Type === StudyType.StudyType_IntraoralXRay
        ? reports.filter(
            (report) => report.Type !== ReportType.ReportType_Pano_Bitewings,
          )
        : reports.filter((report) =>
            ALLOWED_REPORT_TYPES.includes(report.Type),
          );
    return first(filteredReports);
  }, [study.Type, reports]);

  const previewImageSrc = getImageSrc(
    firstRelevantReport?.DisplayAssetID,
    'preview',
  );

  const studyType = study.Type;

  const showUploadingStatusFrame =
    study.Status !== UploadSessionStatus.UploadSessionStatusSuccessful;

  const previewType = useMemo(() => {
    if (showUploadingStatusFrame) {
      return 'uploadingStatusFrame';
    }

    if (
      studyType === StudyType.StudyType_PanoramicXRay &&
      firstRelevantReport?.Specific.case === 'DataPanoBitewings'
    ) {
      return 'panowingsPreview';
    }

    if (studyType === StudyType.StudyType_IntraoralXRay) {
      return 'IOXRayPreview';
    }

    return 'simplePreview';
  }, [studyType, firstRelevantReport, showUploadingStatusFrame]);

  const isIOXRayOrPanowingsPreview =
    previewType === 'IOXRayPreview' || previewType === 'panowingsPreview';

  const showStudyPreview =
    previewType !== 'uploadingStatusFrame' && previewImageSrc;

  const backgroundStyles = showStudyPreview
    ? styles.blackBackground
    : styles.whiteBackground;

  const imgTextColorStyle = showStudyPreview
    ? styles.imgTextWhite
    : styles.imgTextBlack;

  return (
    <div
      ref={imagePreviewRef}
      data-component="study-preview"
      className={cn(styles.imgContainer, backgroundStyles, className)}
    >
      {!showStudyPreview && <UploadingStatusFrame study={study} />}

      {showStudyPreview && (
        <img
          className={cn(
            styles.img,
            isIOXRayOrPanowingsPreview && styles.IOXRayOrPanowingsPreview,
          )}
          onError={ensureRefreshImageSrc}
          src={previewImageSrc}
          alt={formatMessage(i18n.studyTypes[study.Type])}
        />
      )}

      {studyType === StudyType.StudyType_IntraoralXRay ? (
        <>
          <div className={styles.layer}>
            <div className={styles.info}>
              <h5
                className={cn(
                  styles.reportType,
                  isPhone ? 'h6' : 'h5',
                  imgTextColorStyle,
                )}
              >
                {formatMessage(studyCardTitle[studyType])}
              </h5>

              {study?.Created?.At && (
                <span
                  className={cn(
                    styles.date,
                    isPhone ? 'p4' : 'p3',
                    imgTextColorStyle,
                  )}
                >
                  {formatDate(study.Created.At.toDate(), {
                    dateStyle: 'medium',
                  })}
                </span>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className={cn(styles.imgText)}>
          <h5 className={cn('h5', imgTextColorStyle)}>
            {formatMessage(studyCardTitle[studyType])}
          </h5>

          {study?.Created?.At && (
            <span className={cn('p3', imgTextColorStyle)}>
              {formatDate(study.Created.At.toDate(), {
                dateStyle: 'medium',
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
